import React, { useState, useEffect } from 'react'
import i18n from 'i18next'
import utils from './utils'
import InfoAccordion from '../InfoAccordion'

const buildMissingTranslationsData = (missingItems, locales) => {
    const data = {}
    locales.forEach(({ label: lang }) => {
        data[lang] = {
            fields: new Map(),
            questionCount: 0,
            fieldCount: 0,
        }
    })

    missingItems.forEach(({
        lang, questionId, field, isOptionField,
    }) => {
        const hasQuestionEntry = data[lang].fields.has(questionId)
        if (!hasQuestionEntry) {
            data[lang].fields.set(questionId, [field])
            data[lang].questionCount += 1
            data[lang].fieldCount += 1
        } else if (!data[lang].fields.get(questionId).includes(field)) {
            data[lang].fields.get(questionId).push(field)
            data[lang].fieldCount += 1
        } else if (isOptionField) { // each option = 1 field
            data[lang].fieldCount += 1
        }
    })

    return data
}

const MissingTranslations = ({ survey, availableLocales, saving }) => {
    const [isAccordionOpen, setIsAccordionOpen] = useState(false)
    const [missingTranslations, setMissingTranslations] = useState({})
    const { getQuestionSections, getContentText, getOptionTitle } = utils

    const getMissingTranslations = () => {
        // welcome, completion and screening texts are left out as they
        // do not fallback to anything in case their translations are missing
        const missingItems = []
        survey.standardPages.forEach((p) => p.questions.forEach((pq) => {
            const { question } = pq
            const sections = getQuestionSections(question).reduce((acc, { content, set }) => {
                if (content) (acc.content ??= []).push(content)
                else if (set) (acc.options ??= []).push(set)
                return acc
            }, {})

            availableLocales.forEach(({ value: locale, label: lang }) => {
                // check all question fields
                sections.content.forEach((field) => {
                    let isMissingTranslation
                    if (['shortTitle', 'longTitle'].includes(field)) {
                        isMissingTranslation = getContentText(question, field, locale).length < 1
                    // } else if (locale !== question.metadata.initialLocale) {
                    } else {
                        // only check [introduction, aboveText, belowText] if original language is not blank
                        const initLocaleText = getContentText(question, field, question.metadata.initialLocale)
                        isMissingTranslation = initLocaleText
                            && initLocaleText !== ''
                            && getContentText(question, field, locale).length < 1
                    }
                    if (isMissingTranslation) {
                        missingItems.push({
                            lang, questionId: question.id, field, isOptionField: false,
                        })
                    }
                })
                // check all question options in optionSet
                if (sections.options) {
                    sections.options.forEach((optionsName) => {
                        const optionTitles = question[optionsName]?.options.map((o) => getOptionTitle(o, locale))

                        if (optionTitles.length > 0) {
                            optionTitles.forEach((ot) => {
                                const isMissingTranslation = ot === ''
                                if (isMissingTranslation) {
                                    missingItems.push({
                                        lang, questionId: question.id, field: optionsName, isOptionField: true,
                                    })
                                }
                            })
                        }
                    })
                }
            })
        }))

        return buildMissingTranslationsData(missingItems, availableLocales)
    }

    useEffect(() => {
        if (!saving) setMissingTranslations(getMissingTranslations())
    }, [saving])

    const hasMissingTranslations = Object.values(missingTranslations).some((lang) => lang.fieldCount > 0)

    return hasMissingTranslations ? (
        <div className="missing-translations-container">
            <InfoAccordion
                cssClass="warning"
                infoContentClass="p-8"
                title={i18n.t('s_fallback_translation_title')}
                show={isAccordionOpen}
                toggle={() => setIsAccordionOpen(!isAccordionOpen)}
            >
                <div className="info-wrapper">
                    <p>{i18n.t('s_fallback_translation_summary')}</p>
                    <table className="missing-translations">
                        <thead>
                            <tr>
                                <th>{i18n.t('language')}</th>
                                {Object.keys(missingTranslations).map((lang) => (
                                    <td key={lang}>{lang}</td>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th>
                                    {i18n.t('s_fallback_translation_table_header2')}
                                </th>
                                {Object.values(missingTranslations).map(({ questionCount, fieldCount }, i) => (
                                    <td key={Object.keys(missingTranslations)[i]}>
                                        {`${questionCount} (${fieldCount})`}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>

            </InfoAccordion>
        </div>
    ) : null
}

export default MissingTranslations
